/** @format */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

function readTokenFromUrl() {
   // Preferred: #token=... (a fragment never leaves the browser)
   const hash = window.location.hash.replace(/^#/, "");
   const fromHash = new URLSearchParams(hash).get("token");
   if (fromHash) return fromHash;
   // Fallback: ?token=... so an older link still works.
   return new URLSearchParams(window.location.search).get("token");
}

/**
 * Handover — signs a merchant in from a link handed over by onboarding staff.
 *
 * The staff app creates the restaurant and mints a merchant token, then shows a
 * link/QR. The owner opens it on their own phone and lands inside the app with
 * no OTP, no Google sign-in and no signup form.
 *
 * The token is read from the URL *fragment* (#token=...) rather than a query
 * string: fragments are never sent to the server, so the credential stays out of
 * access logs, the Referer header and any proxy in between. We also strip it
 * from the address bar as soon as it is stored, so it does not linger in history
 * or get shared when the owner sends someone the URL.
 */
function Handover() {
   const navigate = useNavigate();
   const [status, setStatus] = useState("working");
   const [message, setMessage] = useState("");

   // Captured during the first render, before any effect can strip the fragment
   // and before React's development double-invocation runs the effect again.
   const [token] = useState(readTokenFromUrl);
   const handled = useRef(false);

   useEffect(() => {
      if (handled.current) return;
      handled.current = true;

      if (!token) {
         setStatus("error");
         setMessage("This link is missing its sign-in code. Ask the TBM team for a new link.");
         return;
      }

      localStorage.setItem("savedT", token);
      localStorage.setItem("waiter_mode", "false");

      // Remove the credential from the address bar before anything else happens.
      window.history.replaceState({}, document.title, "/handover");

      // Confirm the backend actually accepts it, so a stale or expired link
      // shows a clear message instead of dropping the owner on a broken screen.
      fetch(`${process.env.REACT_APP_DEV_URL}/merchant/`, {
         headers: {
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "any",
         },
      })
         .then((response) => {
            if (response.status === 401) throw new Error("expired");
            if (!response.ok) throw new Error("unavailable");
            return response.json();
         })
         .then((merchant) => {
            setStatus("done");
            setMessage(merchant?.restaurant_name || "");
            setTimeout(() => navigate("/orders"), 900);
         })
         .catch((error) => {
            localStorage.removeItem("savedT");
            setStatus("error");
            setMessage(
               error.message === "expired"
                  ? "This link has expired. Ask the TBM team to send a new one."
                  : "We could not reach the server. Check your connection and open the link again.",
            );
         });
   }, [navigate, token]);

   return (
      <div
         style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            fontFamily: "system-ui, sans-serif",
            textAlign: "center",
         }}
      >
         <div style={{ maxWidth: 320 }}>
            {status === "working" && (
               <>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>🍽️</div>
                  <h2 style={{ margin: 0, fontSize: 18 }}>Setting up your restaurant…</h2>
                  <p style={{ color: "#666", fontSize: 14 }}>This only takes a moment.</p>
               </>
            )}

            {status === "done" && (
               <>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                  <h2 style={{ margin: 0, fontSize: 18 }}>
                     {message ? `Welcome, ${message}` : "You're signed in"}
                  </h2>
                  <p style={{ color: "#666", fontSize: 14 }}>Taking you to your orders…</p>
               </>
            )}

            {status === "error" && (
               <>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                  <h2 style={{ margin: 0, fontSize: 18 }}>Could not sign you in</h2>
                  <p style={{ color: "#666", fontSize: 14 }}>{message}</p>
                  <button
                     onClick={() => navigate("/")}
                     style={{
                        marginTop: 16,
                        padding: "10px 16px",
                        borderRadius: 8,
                        border: "1px solid #ccc",
                        background: "#fff",
                        cursor: "pointer",
                     }}
                  >
                     Go to sign in
                  </button>
               </>
            )}
         </div>
      </div>
   );
}

export default Handover;
